/**
 * 渠道趋势表格子组件
 * 对应板块: 板块 D — 在投广告渠道分析
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_EC, DBO_TM, DBO_V } from '@op/utils/format';
import type { TRequestBackType } from '@op/utils/requestUtil';
import type { TYAxisFormat } from './types';
import useLanguage from '@op/hooks/useLanguage';
import ChannelLabel from '@op/components/ChannelLabel';
import { dealWithOthersName } from '@op/utils/languageValue';

interface IChannelTrendTableProps {
  data: TRequestBackType;
  loading?: boolean;
  valueFormat: TYAxisFormat;
  height: number;
  filter?: any;
}

const ChannelTrendTable: React.FC<IChannelTrendTableProps> = ({
  data,
  loading,
  valueFormat,
  height,
  filter,
}) => {
  const { formatMessage } = useLanguage();
  // ==================== 数值格式化 ====================
  const valueFormatter = useMemo(() => {
    const formatters: Record<TYAxisFormat, (v: number) => string> = {
      integer: (v) => DBO_V.reviews(v),
      percent: (v) => DBO_V.ratio_min(v),
    };
    return formatters[valueFormat];
  }, [valueFormat]);

  // ==================== 表格数据 ====================
  const tableData = useMemo(() => {
    // 优先使用 tableRecord
    if (data?.tableRecord?.length) {
      // 为每条数据添加唯一标识 id
      return data.tableRecord.map((row, index) => ({
        ...row,
        id: row.time || index,
      }));
    }

    // 降级：从 series 和 xAxis 转换
    if (!data?.xAxis || !data?.series) return [];

    return data.xAxis.map((time, index) => {
      const row: any = { time, id: time || index };
      data.series?.forEach((s) => {
        row[s.name] = s.data[index];
      });
      return row;
    });
  }, [data]);

  // ==================== 表格列配置 ====================
  const columns = useMemo(() => {
    if (!tableData?.length) return [];

    const colList: any[] = [
      {
        key: 'time',
        header: formatMessage('Date'),
        dataIndex: 'time',
        width: filter?.date_type === 'weekly' ? 220 : 160,
        render: (value: any) =>
          DBO_EC.toolTime(value.time, filter?.date_type || 'daily'),
      },
    ];

    // 动态添加渠道列
    if (data?.series?.length) {
      data.series.forEach((s) => {
        colList.push({
          key: s.name,
          header: (
            <ChannelLabel
              channel_name={s.name as any}
              showText={true}
              isMarketing={true}
              iconStyle={{ width: 16, height: 16, marginRight: 4 }}
              replaceText={dealWithOthersName(s.name, 'channel')}
            />
          ),
          dataIndex: s.name,
          width: 160,
          align: 'right',
          render: (value: any) => valueFormatter?.(value?.[s.name]),
        });
      });
    }

    return colList;
  }, [tableData, data?.series, valueFormatter, formatMessage, filter]);

  // ==================== 动态 minWidth ====================
  const tableMinWidth = useMemo(() => {
    if (!columns.length) return 1400;
    const totalColWidth = columns.reduce((sum, col) => sum + (col.width || 160), 0);
    // 列多时保持默认 minWidth 防止挤压，列少时自适应实际宽度
    return Math.min(totalColWidth, 1400);
  }, [columns]);

  // ==================== 渲染 ====================
  return (
    <div className="dbo-channel-trend-table">
      <TableCommonHook
        columns={columns}
        records={tableData}
        total={tableData.length}
        loading={loading}
        hasPageable={false}
        tableSticky={true}
        maxHeight={height - 49.5}
        height={height - 49.5}
        minWidth={tableMinWidth}
      />
    </div>
  );
};

export default React.memo(ChannelTrendTable);
